import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
    computeReorderedStepIds,
    buildConditionLabel,
    buildAutoOutcomeInfo,
} from './flowTransforms.js';
import postReorderSteps from '../../functions/API/postReorderSteps.js';
import postUpdateStep from '../../functions/API/postUpdateStep.js';
import postAddStepToTaskFlow from '../../functions/API/postAddStepToTaskFlow.js';
import postUpdateTaskFlow from '../../functions/API/postUpdateTaskFlow.js';
import getSharepointSites from '../../functions/API/getSharepointSites.js';
import getSharepointLibraries from '../../functions/API/getSharepointLibraries.js';
import getAdobeSignTemplates from '../../functions/API/getAdobeSignTemplates.js';
import getAdobeSignTemplateFields from '../../functions/API/getAdobeSignTemplateFields.js';
import getAPI from '../../functions/API/getAPI.js';
import ReactSelect from 'react-select';
import { toast } from '../../lib/toast.js';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { FIELD_TYPES } from '../../constants/fieldTypes.js';
import {
    Flag,
    Plus,
    X,
    ChevronUp,
    ChevronDown,
    MoreVertical,
    Pencil,
    Trash2,
    CreditCard,
    Upload,
    Zap,
    ClipboardList,
    Save,
    Eye,
    ShieldCheck,
    Info,
    RefreshCw,
    PenTool,
    Loader2,
} from 'lucide-react';

const ALLOWED_FILE_TYPES = [
    { value: 'pdf', label: 'PDF' },
    { value: 'jpg', label: 'JPG' },
    { value: 'png', label: 'PNG' },
    { value: 'gif', label: 'GIF' },
    { value: 'doc', label: 'DOC' },
    { value: 'docx', label: 'DOCX' },
    { value: 'xls', label: 'XLS' },
    { value: 'xlsx', label: 'XLSX' },
];

// ─── AddStepButton ──────────────────────────────────────────────────

function AddStepButton({ paymentsEnabled, sharepointEnabled, onAddStep, branchCondition, insertPosition }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handle = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [menuOpen]);

    const handleAdd = (type) => {
        setMenuOpen(false);
        onAddStep?.(type, branchCondition || null, insertPosition);
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!menuOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setOpenUpward(spaceBelow < 200);
        }
        setMenuOpen(!menuOpen);
    };

    return (
        <div className="flex justify-center py-1.5">
            <div className="relative" ref={menuRef}>
                <button
                    ref={buttonRef}
                    className="w-6 h-6 rounded-full border-2 border-dashed border-gray-300 dark:border-zinc-600 text-gray-400 dark:text-zinc-500 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center justify-center transition-all cursor-pointer"
                    onClick={handleClick}
                    title="Add step"
                >
                    <Plus className="h-3 w-3" />
                </button>
                {menuOpen && (
                    <div className={`absolute left-1/2 -translate-x-1/2 bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700 shadow-lg py-1 z-50 min-w-[160px] ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                        <button
                            className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleAdd('standard'); }}
                        >
                            <ClipboardList className="h-3.5 w-3.5 text-blue-600" />
                            Standard Step
                        </button>
                        {paymentsEnabled && (
                            <button
                                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                                onClick={(e) => { e.stopPropagation(); handleAdd('payment'); }}
                            >
                                <CreditCard className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                                Payment Step
                            </button>
                        )}
                        {sharepointEnabled && (
                            <button
                                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                                onClick={(e) => { e.stopPropagation(); handleAdd('document_upload'); }}
                            >
                                <Upload className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                                Document Upload
                            </button>
                        )}
                        <button
                            className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleAdd('document_signing'); }}
                        >
                            <PenTool className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                            Document Signing
                        </button>
                        <button
                            className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleAdd('info'); }}
                        >
                            <Info className="h-3.5 w-3.5 text-blue-600" />
                            Information
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── StepRow ────────────────────────────────────────────────────────

function StepRow({
    step,
    stepNumber,
    totalSteps,
    allSteps,
    isExpanded,
    onExpand,
    onCollapse,
    onDelete,
    onMoveUp,
    onMoveDown,
    onAddField,
    onEditField,
    onDeleteField,
    onQuickUpdate,
    onAddCondition,
    onEditCondition,
    onAddAutoOutcome,
    onEditAutoOutcome,
    adminUsers,
}) {
    const hasConditions = step.conditional_logic?.length > 0;
    const conditionLabel = hasConditions ? buildConditionLabel(step.conditional_logic, allSteps) : null;
    const hasAutoOutcome = step.auto_outcome_rules?.length > 0;
    const autoOutcomeInfo = hasAutoOutcome ? buildAutoOutcomeInfo(step.auto_outcome_rules, allSteps) : { label: null, detail: null };
    const fields = step.fields || [];
    const fieldCount = fields.length;

    const [editValues, setEditValues] = useState({
        name: step.name,
        description: step.description,
        actor_type: step.actor_type,
        step_config: step.step_config || null,
        requires_admin_approval: step.requires_admin_approval ?? false,
        help_url: step.help_url || '',
        help_text: step.help_text || '',
        on_complete_reassign_admin_user_id: step.on_complete_reassign_admin_user_id ?? null,
    });

    const [useDefaultClientCode, setUseDefaultClientCode] = useState(
        !step.step_config?.client_code
    );

    const [spSites, setSpSites] = useState([]);
    const [spLibraries, setSpLibraries] = useState([]);

    // Adobe Sign state for document_signing steps
    const [asTemplates, setAsTemplates] = useState([]);
    const [asTemplateFields, setAsTemplateFields] = useState([]);
    const [asLoadingFields, setAsLoadingFields] = useState(false);

    useEffect(() => {
        if (step.step_type !== 'document_upload' || !isExpanded) return;
        getSharepointSites().then(res => {
            const sites = res?.data?.sites || res?.data || [];
            setSpSites(Array.isArray(sites) ? sites : []);
        }).catch(() => setSpSites([]));
    }, [step.step_type, isExpanded]);

    useEffect(() => {
        const siteId = editValues.step_config?.sharepoint_site_id;
        if (!siteId || step.step_type !== 'document_upload' || !isExpanded) return;
        getSharepointLibraries(siteId).then(res => {
            const libs = res?.data?.libraries || res?.data || [];
            setSpLibraries(Array.isArray(libs) ? libs : []);
        }).catch(() => setSpLibraries([]));
    }, [editValues.step_config?.sharepoint_site_id, step.step_type, isExpanded]);

    useEffect(() => {
        if (step.step_type !== 'document_signing' || !isExpanded) return;
        getAdobeSignTemplates().then(res => {
            const templates = res?.data?.libraryDocumentList || [];
            setAsTemplates(Array.isArray(templates) ? templates : []);
        }).catch(() => setAsTemplates([]));
    }, [step.step_type, isExpanded]);

    useEffect(() => {
        const templateId = editValues.step_config?.template_id;
        if (!templateId || step.step_type !== 'document_signing' || !isExpanded) return;
        setAsLoadingFields(true);
        getAdobeSignTemplateFields(templateId).then(res => {
            const fields = res?.data?.fields || [];
            setAsTemplateFields(Array.isArray(fields) ? fields : []);
        }).catch(() => setAsTemplateFields([])).finally(() => setAsLoadingFields(false));
    }, [editValues.step_config?.template_id, step.step_type, isExpanded]);

    const stepTypeLabels = { standard: 'Standard', payment: 'Payment', document_upload: 'Doc Upload', document_signing: 'Doc Signing', info: 'Information' };
    const stepTypeIcons = { standard: ClipboardList, payment: CreditCard, document_upload: Upload, document_signing: PenTool, info: Info };
    const StepIcon = stepTypeIcons[step.step_type] || ClipboardList;

    const handleSave = useCallback((e) => {
        e.stopPropagation();
        onQuickUpdate?.(step.id, editValues);
    }, [onQuickUpdate, step.id, editValues]);

    // ── Collapsed View ──
    if (!isExpanded) {
        return (
            <div
                className="bg-white dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700 overflow-hidden hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group"
                onClick={(e) => { e.stopPropagation(); onExpand?.(step.id); }}
            >
                {/* Condition banner */}
                {hasConditions && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 border-b border-purple-200 dark:border-purple-800">
                        <Eye className="h-3 w-3 text-purple-600 dark:text-purple-400 shrink-0" />
                        <span className="text-xs font-medium text-purple-700 dark:text-purple-400 truncate">Shown when: {conditionLabel}</span>
                    </div>
                )}

                {/* Card header bar */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-zinc-800 border-b border-gray-100 dark:border-zinc-700">
                    <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-xs font-bold text-gray-500 dark:text-gray-400 shrink-0">
                            {stepNumber}
                        </span>
                        <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-white dark:bg-zinc-800">
                            <StepIcon className="mr-0.5 h-3 w-3" />
                            {stepTypeLabels[step.step_type] || step.step_type}
                        </Badge>
                        <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-white dark:bg-zinc-800">
                            {step.actor_type === 'admin' ? 'Admin' : 'Tenant'}
                        </Badge>
                        {step.step_type === 'standard' && fieldCount > 0 && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">{fieldCount} fields</span>
                        )}
                        {step.requires_admin_approval && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800">
                                <ShieldCheck className="mr-0.5 h-3 w-3" />
                                Approval
                            </Badge>
                        )}
                        {step.on_complete_reassign_admin_user_id && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800">
                                <RefreshCw className="mr-0.5 h-3 w-3" />
                                Reassign
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 disabled:opacity-30"
                            onClick={(e) => { e.stopPropagation(); onMoveUp?.(step.id); }}
                            disabled={stepNumber === 1}
                            title="Move up"
                        >
                            <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                        </button>
                        <button
                            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 disabled:opacity-30"
                            onClick={(e) => { e.stopPropagation(); onMoveDown?.(step.id); }}
                            disabled={stepNumber === totalSteps}
                            title="Move down"
                        >
                            <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                        </button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <MoreVertical className="h-3.5 w-3.5 text-gray-500" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onSelect={() => onDelete?.(step)}
                                    className="text-red-600 focus:text-red-600"
                                >
                                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Card body - step name and description */}
                <div className="px-3 py-2.5">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">{step.name}</h3>
                    {step.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{step.description}</p>
                    )}
                    {/* Payment step summary */}
                    {step.step_type === 'payment' && step.step_config && (
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-green-700 dark:text-green-400">
                            <span className="capitalize">{step.step_config.provider || 'allpay'}</span>
                            {step.step_config.description && (
                                <span className="text-gray-400 dark:text-gray-500 truncate">{step.step_config.description}</span>
                            )}
                        </div>
                    )}
                    {/* Document signing step summary */}
                    {step.step_type === 'document_signing' && step.step_config && (
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-amber-700 dark:text-amber-400">
                            <span>{step.step_config.template_name || 'No template selected'}</span>
                            {step.step_config.field_mappings?.length > 0 && (
                                <span className="text-gray-400 dark:text-gray-500">{step.step_config.field_mappings.length} field(s) mapped</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Auto-outcome banner */}
                {hasAutoOutcome && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 border-t border-amber-200 dark:border-amber-800">
                        <Zap className="h-3 w-3 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-400 truncate">{autoOutcomeInfo.label}</span>
                        <span className="text-xs text-amber-500 truncate hidden sm:inline">&mdash; {autoOutcomeInfo.detail}</span>
                    </div>
                )}
            </div>
        );
    }

    // ── Expanded View ──
    return (
        <div className="bg-white dark:bg-zinc-800 rounded-lg border-2 border-blue-400 shadow-md" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-zinc-700">
                <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-xs font-bold text-blue-700 shrink-0">
                        {stepNumber}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{step.name}</h3>
                    <Badge variant="outline" className="text-xs px-1.5 py-0 h-5">
                        <StepIcon className="mr-0.5 h-3 w-3" />
                        {stepTypeLabels[step.step_type]}
                    </Badge>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" onClick={handleSave}>
                        <Save className="mr-1.5 h-3 w-3" />
                        Save
                    </Button>
                    <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-700" onClick={() => onCollapse?.()}>
                        <X className="h-4 w-4 text-gray-400" />
                    </button>
                </div>
            </div>

            {/* Form Content */}
            <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                {/* Core fields */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name</label>
                        <input
                            type="text"
                            value={editValues.name}
                            onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                            className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Actor</label>
                        <select
                            value={editValues.actor_type}
                            onChange={(e) => setEditValues({ ...editValues, actor_type: e.target.value })}
                            className="block w-full rounded-md border-0 py-1.5 pl-2.5 pr-8 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <option value="tenant">Tenant</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {step.step_type === 'info' ? `Content (shown to ${editValues.actor_type === 'admin' ? 'admin' : 'tenant'})` : 'Description'}
                    </label>
                    <textarea
                        value={editValues.description || ''}
                        onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                        rows={step.step_type === 'info' ? 4 : 2}
                        placeholder={step.step_type === 'info' ? `Enter the information or instructions the ${editValues.actor_type === 'admin' ? 'admin' : 'tenant'} must review` : ''}
                        className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>

                {/* Admin approval toggle — only relevant for tenant-assigned steps */}
                {editValues.actor_type !== 'admin' && (
                <div className="flex items-center gap-3 py-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <Switch
                            checked={editValues.requires_admin_approval}
                            onCheckedChange={(v) => setEditValues({ ...editValues, requires_admin_approval: v })}
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">Requires admin approval</span>
                    </label>
                    {editValues.requires_admin_approval && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Tenant submissions will be held for admin review</span>
                    )}
                </div>
                )}

                {/* Auto-reassign on completion */}
                <div className="flex items-center gap-3 py-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <Switch
                            checked={!!editValues.on_complete_reassign_admin_user_id}
                            onCheckedChange={(v) => setEditValues({ ...editValues, on_complete_reassign_admin_user_id: v ? (adminUsers?.[0]?.id || null) : null })}
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">Auto-reassign on completion</span>
                    </label>
                </div>
                {editValues.on_complete_reassign_admin_user_id && (
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                        <label className="block text-xs font-medium text-indigo-800 mb-1.5">
                            <RefreshCw className="inline h-3 w-3 mr-1" />
                            Reassign to
                        </label>
                        <div onClick={(e) => e.stopPropagation()}>
                            <ReactSelect
                                value={(adminUsers || []).map(u => ({ value: u.id, label: `${u.firstname} ${u.lastname}` })).find(o => o.value === editValues.on_complete_reassign_admin_user_id) || null}
                                onChange={(option) => setEditValues({ ...editValues, on_complete_reassign_admin_user_id: option ? option.value : null })}
                                options={(adminUsers || []).map(u => ({ value: u.id, label: `${u.firstname} ${u.lastname}` }))}
                                placeholder="Search for an admin..."
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                className="text-sm"
                            />
                        </div>
                        <p className="mt-1.5 text-xs text-indigo-700/70">Instance ownership will transfer to this admin when this step is completed</p>
                    </div>
                )}

                {/* Help text & URL for info steps (standard steps configure per-field) */}
                {step.step_type === 'info' && (
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Help Text</label>
                        <input
                            type="text"
                            value={editValues.help_text || ''}
                            onChange={(e) => setEditValues({ ...editValues, help_text: e.target.value })}
                            placeholder="Brief guidance for this step"
                            className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Help URL</label>
                        <input
                            type="url"
                            value={editValues.help_url || ''}
                            onChange={(e) => setEditValues({ ...editValues, help_url: e.target.value })}
                            placeholder="https://..."
                            className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
                )}

{/* Payment config */}
                {step.step_type === 'payment' && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800 space-y-3">
                        <h4 className="text-xs font-semibold text-green-800 dark:text-green-300 flex items-center gap-1.5">
                            <CreditCard className="h-3.5 w-3.5" />
                            Payment Configuration
                        </h4>
                        <div>
                            <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-1">Payment Provider</label>
                            <select
                                value={editValues.step_config?.provider ?? 'allpay'}
                                onChange={(e) => setEditValues({
                                    ...editValues,
                                    step_config: { ...editValues.step_config, provider: e.target.value },
                                })}
                                className="block w-full rounded-md border-0 py-1.5 pl-2.5 pr-8 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <option value="allpay">AllPay</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-1">Payment Description (optional)</label>
                            <input
                                type="text"
                                value={editValues.step_config?.description ?? ''}
                                onChange={(e) => setEditValues({
                                    ...editValues,
                                    step_config: { ...editValues.step_config, description: e.target.value || null },
                                })}
                                placeholder="e.g., Application fee"
                                className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                onClick={(e) => e.stopPropagation()}
                            />
                            <p className="mt-0.5 text-xs text-green-700/70 dark:text-green-400/70">Shown to the user during payment</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-1">Help Text</label>
                                <input
                                    type="text"
                                    value={editValues.help_text || ''}
                                    onChange={(e) => setEditValues({ ...editValues, help_text: e.target.value })}
                                    placeholder="Brief guidance for this step"
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-green-800 dark:text-green-300 mb-1">Help URL</label>
                                <input
                                    type="url"
                                    value={editValues.help_url || ''}
                                    onChange={(e) => setEditValues({ ...editValues, help_url: e.target.value })}
                                    placeholder="https://..."
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                        <div className="border-t border-green-200 dark:border-green-800 pt-3">
                            <div className="flex items-center justify-between mb-1.5">
                                <div>
                                    <span className="text-xs font-medium text-green-800 dark:text-green-300">Client Code</span>
                                    <p className="text-xs text-green-700/70 dark:text-green-400/70">Which payment account to use</p>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Use default</span>
                                    <Switch
                                        checked={useDefaultClientCode}
                                        onCheckedChange={(checked) => {
                                            setUseDefaultClientCode(checked);
                                            if (checked) {
                                                setEditValues({
                                                    ...editValues,
                                                    step_config: { ...editValues.step_config, client_code: null },
                                                });
                                            }
                                        }}
                                    />
                                </label>
                            </div>
                            {!useDefaultClientCode && (
                                <input
                                    type="text"
                                    value={editValues.step_config?.client_code ?? ''}
                                    onChange={(e) => setEditValues({
                                        ...editValues,
                                        step_config: { ...editValues.step_config, client_code: e.target.value || null },
                                    })}
                                    placeholder="Enter custom client code"
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            )}
                            {useDefaultClientCode && (
                                <p className="text-xs text-green-700/70 dark:text-green-400/70">Payments will use the client code configured in Settings</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Document upload config */}
                {step.step_type === 'document_upload' && (
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-800 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 mb-1">SharePoint Site</label>
                                <select
                                    value={editValues.step_config?.sharepoint_site_id ?? ''}
                                    onChange={(e) => {
                                        const siteId = parseInt(e.target.value) || null;
                                        setEditValues({
                                            ...editValues,
                                            step_config: { ...editValues.step_config, sharepoint_site_id: siteId, sharepoint_library_id: null },
                                        });
                                        setSpLibraries([]);
                                    }}
                                    className="block w-full rounded-md border-0 py-1.5 pl-2.5 pr-8 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <option value="">Select site...</option>
                                    {spSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 mb-1">Library</label>
                                <select
                                    value={editValues.step_config?.sharepoint_library_id ?? ''}
                                    onChange={(e) => setEditValues({
                                        ...editValues,
                                        step_config: { ...editValues.step_config, sharepoint_library_id: parseInt(e.target.value) || null },
                                    })}
                                    className="block w-full rounded-md border-0 py-1.5 pl-2.5 pr-8 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={!editValues.step_config?.sharepoint_site_id}
                                >
                                    <option value="">{editValues.step_config?.sharepoint_site_id ? 'Select library...' : 'Select a site first'}</option>
                                    {spLibraries.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 mb-1">Max Files</label>
                                <input
                                    type="number" min="1" max="20"
                                    value={editValues.step_config?.max_files ?? 5}
                                    onChange={(e) => setEditValues({
                                        ...editValues,
                                        step_config: { ...editValues.step_config, max_files: parseInt(e.target.value) || 5 },
                                    })}
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 mb-1">Max Size (MB)</label>
                                <input
                                    type="number" min="1" max="100"
                                    value={editValues.step_config?.max_size_mb ?? 20}
                                    onChange={(e) => setEditValues({
                                        ...editValues,
                                        step_config: { ...editValues.step_config, max_size_mb: parseInt(e.target.value) || 20 },
                                    })}
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                        <div className="border-t border-orange-200 dark:border-orange-800 pt-3">
                            <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 mb-2">Allowed File Types</label>
                            <div className="flex flex-wrap gap-1.5">
                                {ALLOWED_FILE_TYPES.map(type => {
                                    const selected = (editValues.step_config?.allowed_types || []).includes(type.value);
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const current = editValues.step_config?.allowed_types || [];
                                                const updated = selected
                                                    ? current.filter(t => t !== type.value)
                                                    : [...current, type.value];
                                                setEditValues({
                                                    ...editValues,
                                                    step_config: { ...editValues.step_config, allowed_types: updated },
                                                });
                                            }}
                                            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                                                selected
                                                    ? 'bg-orange-600 text-white border-orange-600'
                                                    : 'bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-zinc-600 hover:border-orange-400'
                                            }`}
                                        >
                                            {type.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mt-1.5 text-xs text-orange-700/70 dark:text-orange-400/70">
                                {(editValues.step_config?.allowed_types || []).length} type{(editValues.step_config?.allowed_types || []).length !== 1 ? 's' : ''} selected
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 mb-1">Help Text</label>
                                <input
                                    type="text"
                                    value={editValues.help_text || ''}
                                    onChange={(e) => setEditValues({ ...editValues, help_text: e.target.value })}
                                    placeholder="Brief guidance for this step"
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 mb-1">Help URL</label>
                                <input
                                    type="url"
                                    value={editValues.help_url || ''}
                                    onChange={(e) => setEditValues({ ...editValues, help_url: e.target.value })}
                                    placeholder="https://..."
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Document signing config */}
                {step.step_type === 'document_signing' && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800 space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">Adobe Sign Template</label>
                            <select
                                value={editValues.step_config?.template_id ?? ''}
                                onChange={(e) => {
                                    const templateId = e.target.value || null;
                                    const template = asTemplates.find(t => t.id === templateId);
                                    setEditValues({
                                        ...editValues,
                                        step_config: {
                                            ...editValues.step_config,
                                            provider: 'adobesign',
                                            template_id: templateId,
                                            template_name: template?.name || '',
                                            field_mappings: [],
                                        },
                                    });
                                }}
                                className="block w-full rounded-md border-0 py-1.5 pl-2.5 pr-8 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <option value="">Select template...</option>
                                {asTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>

                        {/* Agreement name */}
                        {editValues.step_config?.template_id && (
                            <div>
                                <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">Agreement Name</label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Uses template name if empty. Use "Insert Field" for dynamic values.</p>
                                <div className="flex gap-1.5">
                                    <input
                                        type="text"
                                        value={editValues.step_config?.agreement_name || ''}
                                        onChange={(e) => setEditValues({ ...editValues, step_config: { ...editValues.step_config, agreement_name: e.target.value } })}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder={editValues.step_config?.template_name || 'Template name'}
                                        className="flex-1 block rounded border-0 py-1 pl-2 text-xs text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600"
                                        id={`agreement-name-${step.id}`}
                                    />
                                    {(() => {
                                        const fieldSteps = allSteps.filter(s => s.step_type === 'standard' && s.fields?.length > 0);
                                        if (fieldSteps.length === 0) return null;
                                        return (
                                            <select
                                                value=""
                                                onChange={(e) => {
                                                    if (!e.target.value) return;
                                                    const input = document.getElementById(`agreement-name-${step.id}`);
                                                    const token = `{{${e.target.value}}}`;
                                                    const current = editValues.step_config?.agreement_name || '';
                                                    const start = input?.selectionStart ?? current.length;
                                                    const end = input?.selectionEnd ?? start;
                                                    const updated = current.slice(0, start) + token + current.slice(end);
                                                    setEditValues({ ...editValues, step_config: { ...editValues.step_config, agreement_name: updated } });
                                                    e.target.value = '';
                                                    setTimeout(() => {
                                                        if (input) {
                                                            input.focus();
                                                            const pos = start + token.length;
                                                            input.setSelectionRange(pos, pos);
                                                        }
                                                    }, 0);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="rounded border-0 py-1 pl-2 pr-6 text-xs text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600 shrink-0"
                                            >
                                                <option value="">Insert Field</option>
                                                {fieldSteps.map(s =>
                                                    s.fields.map(f => (
                                                        <option key={`${s.id}-${f.id}`} value={f.name}>{s.name} → {f.name}</option>
                                                    ))
                                                )}
                                            </select>
                                        );
                                    })()}
                                </div>
                                {editValues.step_config?.agreement_name && (
                                    <div className="mt-1.5 px-2 py-1 bg-gray-50 dark:bg-zinc-800 rounded text-xs text-gray-500 dark:text-gray-400">
                                        Preview: <span className="text-gray-900 dark:text-white font-medium">
                                            {editValues.step_config.agreement_name.replace(/\{\{(.+?)\}\}/g, (_, name) => `[${name}]`)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Multi-Participant Signing */}
                        {editValues.step_config?.template_id && (
                            <div className="border-t border-amber-200 dark:border-amber-800 pt-3">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-xs font-medium text-amber-800 dark:text-amber-300">Require Admin Counter-Signature</label>
                                    <input
                                        type="checkbox"
                                        checked={editValues.step_config?.require_admin_countersign || false}
                                        onChange={(e) => setEditValues({ ...editValues, step_config: { ...editValues.step_config, require_admin_countersign: e.target.checked } })}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-amber-600 dark:text-amber-400 focus:ring-amber-600"
                                    />
                                </div>
                                {editValues.step_config?.require_admin_countersign && (
                                    <div className="mt-1">
                                        <select
                                            value={editValues.step_config?.signing_order || 'customer_first'}
                                            onChange={(e) => setEditValues({ ...editValues, step_config: { ...editValues.step_config, signing_order: e.target.value } })}
                                            className="block w-full rounded-md border-0 py-1 pl-2 pr-8 text-xs text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <option value="customer_first">Customer signs first, then admin</option>
                                            <option value="admin_first">Admin signs first, then customer</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Field mappings */}
                        {editValues.step_config?.template_id && (
                            <div className="border-t border-amber-200 dark:border-amber-800 pt-3">
                                <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-2">Field Mappings</label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Configure how template fields are pre-filled.</p>
                                {asLoadingFields ? (
                                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        Loading fields...
                                    </div>
                                ) : asTemplateFields.length === 0 ? (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 italic">No form fields in this template</p>
                                ) : (
                                    <div className="space-y-2">
                                        {asTemplateFields.map((field) => {
                                            const mappings = editValues.step_config?.field_mappings || [];
                                            const mapping = mappings.find(m => m.adobe_field_name === field.name) || { adobe_field_name: field.name, source_type: 'customer_enters' };

                                            const updateMapping = (key, val) => {
                                                const currentMappings = [...(editValues.step_config?.field_mappings || [])];
                                                const idx = currentMappings.findIndex(m => m.adobe_field_name === field.name);
                                                const updated = { ...mapping, [key]: val };
                                                if (key === 'source_type') {
                                                    updated.source_step_id = null;
                                                    updated.source_field_id = null;
                                                    updated.static_value = null;
                                                }
                                                if (idx >= 0) {
                                                    currentMappings[idx] = updated;
                                                } else {
                                                    currentMappings.push(updated);
                                                }
                                                setEditValues({
                                                    ...editValues,
                                                    step_config: { ...editValues.step_config, field_mappings: currentMappings },
                                                });
                                            };

                                            const sourceStep = allSteps.find(s => s.id === mapping.source_step_id && s.step_type === 'standard');

                                            const fieldSignerLabel = (() => {
                                                if (!field.assignee || !editValues.step_config?.require_admin_countersign) return 'Customer';
                                                const signerNum = parseInt(field.assignee.replace(/\D/g, ''), 10);
                                                const isFirst = signerNum === 0;
                                                const order = editValues.step_config?.signing_order || 'customer_first';
                                                return isFirst
                                                    ? (order === 'customer_first' ? 'Customer' : 'Admin')
                                                    : (order === 'customer_first' ? 'Admin' : 'Customer');
                                            })();

                                            return (
                                                <div key={field.name} className="p-2 bg-white dark:bg-zinc-800 rounded border border-amber-200 dark:border-amber-800">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-xs font-medium text-amber-800 dark:text-amber-300">
                                                            {field.displayLabel || field.name}
                                                            {field.required && <span className="text-red-500 ml-0.5">*</span>}
                                                        </span>
                                                        {field.assignee && editValues.step_config?.require_admin_countersign && (
                                                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${fieldSignerLabel === 'Customer' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                                                {fieldSignerLabel}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <select
                                                        value={mapping.source_type}
                                                        onChange={(e) => updateMapping('source_type', e.target.value)}
                                                        className="block w-full rounded-md border-0 py-1 pl-2 pr-8 text-xs text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600 mb-1"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <option value="customer_enters">{fieldSignerLabel} enters during signing</option>
                                                        <option value="task_data">Pre-fill from task data</option>
                                                        <option value="static_value">Static value</option>
                                                    </select>

                                                    {mapping.source_type === 'task_data' && (
                                                        <div className="space-y-1 mt-1">
                                                            <select
                                                                value={mapping.source_step_id ?? ''}
                                                                onChange={(e) => updateMapping('source_step_id', parseInt(e.target.value) || null)}
                                                                className="block w-full rounded-md border-0 py-1 pl-2 pr-8 text-xs text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <option value="">Select step...</option>
                                                                {allSteps.filter(s => s.step_type === 'standard').map(s => (
                                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                                ))}
                                                            </select>
                                                            {mapping.source_step_id && sourceStep?.fields && (
                                                                <select
                                                                    value={mapping.source_field_id ?? ''}
                                                                    onChange={(e) => updateMapping('source_field_id', parseInt(e.target.value) || null)}
                                                                    className="block w-full rounded-md border-0 py-1 pl-2 pr-8 text-xs text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <option value="">Select field...</option>
                                                                    {sourceStep.fields.map(f => (
                                                                        <option key={f.id} value={f.id}>{f.name} ({f.field_type})</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                        </div>
                                                    )}

                                                    {mapping.source_type === 'static_value' && (
                                                        <input
                                                            type="text"
                                                            value={mapping.static_value || ''}
                                                            onChange={(e) => updateMapping('static_value', e.target.value)}
                                                            placeholder="Enter value"
                                                            className="mt-1 block w-full rounded-md border-0 py-1 px-2 text-xs text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">Help Text</label>
                                <input
                                    type="text"
                                    value={editValues.help_text || ''}
                                    onChange={(e) => setEditValues({ ...editValues, help_text: e.target.value })}
                                    placeholder="Brief guidance for this step"
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">Help URL</label>
                                <input
                                    type="url"
                                    value={editValues.help_url || ''}
                                    onChange={(e) => setEditValues({ ...editValues, help_url: e.target.value })}
                                    placeholder="https://..."
                                    className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Fields section (standard steps only) */}
                {step.step_type === 'standard' && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Fields</h4>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); onAddField?.(step); }}
                            >
                                <Plus className="mr-1 h-3 w-3" />
                                Add Field
                            </Button>
                        </div>
                        {fields.length === 0 ? (
                            <p className="text-xs text-gray-400 dark:text-gray-500 py-2">No fields yet. Add a field to collect information.</p>
                        ) : (
                            <div className="space-y-1">
                                {fields.map((field, idx) => {
                                    const fieldType = FIELD_TYPES[field.field_type];
                                    const FieldIcon = fieldType?.icon;
                                    return (
                                        <div key={field.id} className="flex items-center gap-2 py-1.5 px-2 rounded bg-gray-50 dark:bg-zinc-800 border border-gray-100 dark:border-zinc-700">
                                            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 w-4 text-center shrink-0">{idx + 1}</span>
                                            <span className="text-xs font-medium text-gray-900 dark:text-white truncate flex-1 min-w-0">
                                                {field.name || field.field_label}
                                            </span>
                                            <Badge variant="outline" className="text-[11px] px-1 py-0 h-4 shrink-0">
                                                {FieldIcon && <FieldIcon className="mr-0.5 h-2.5 w-2.5" />}
                                                {fieldType?.label || field.field_type}
                                            </Badge>
                                            {field.is_required && (
                                                <span className="text-[11px] text-red-500 shrink-0">Req</span>
                                            )}
                                            <button
                                                className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 shrink-0"
                                                onClick={(e) => { e.stopPropagation(); onEditField?.(field); }}
                                                title="Edit field"
                                            >
                                                <Pencil className="h-3 w-3 text-gray-400" />
                                            </button>
                                            <button
                                                className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 shrink-0"
                                                onClick={(e) => { e.stopPropagation(); onDeleteField?.(field); }}
                                                title="Delete field"
                                            >
                                                <Trash2 className="h-3 w-3 text-red-400" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Visibility condition */}
                <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Visibility Condition</h4>
                    {hasConditions ? (
                        <div className="flex items-start justify-between gap-2 p-2.5 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-800">
                            <div className="flex items-start gap-2 min-w-0">
                                <Eye className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400 shrink-0 mt-0.5" />
                                <span className="text-xs text-purple-800 dark:text-purple-300">{conditionLabel}</span>
                            </div>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="shrink-0 text-purple-700 dark:text-purple-400 hover:text-purple-900 hover:bg-purple-100 dark:hover:bg-purple-900/30 h-6 px-2"
                                onClick={(e) => { e.stopPropagation(); onEditCondition?.(step); }}
                            >
                                <Pencil className="h-3 w-3" />
                            </Button>
                        </div>
                    ) : (
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                            onClick={(e) => { e.stopPropagation(); onAddCondition?.(step); }}
                        >
                            <Plus className="mr-1 h-3 w-3" />
                            Add Condition
                        </Button>
                    )}
                </div>

                {/* Auto-outcome rules */}
                <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Auto-Outcome Rules</h4>
                    {hasAutoOutcome ? (
                        <div className="space-y-1">
                            {step.auto_outcome_rules.map((rule, ruleIndex) => (
                                <div
                                    key={ruleIndex}
                                    className="flex items-center justify-between gap-2 p-2 bg-amber-50 dark:bg-amber-900/30 rounded-md border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); onEditAutoOutcome?.(step, ruleIndex); }}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Zap className="h-3 w-3 text-amber-600 dark:text-amber-400 shrink-0" />
                                        <span className="text-xs text-amber-800 dark:text-amber-300 truncate">
                                            Auto: {rule.outcome === 'positive' ? 'Complete' : 'Close'}
                                        </span>
                                    </div>
                                    <Pencil className="h-3 w-3 text-amber-600 dark:text-amber-400 shrink-0" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                            onClick={(e) => { e.stopPropagation(); onAddAutoOutcome?.(step); }}
                        >
                            <Plus className="mr-1 h-3 w-3" />
                            Add Rule
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── OutcomeCard ────────────────────────────────────────────────────

function OutcomeCard({ positiveMessage, negativeMessage, onSaveOutcome }) {
    const [expanded, setExpanded] = useState(false);
    const [positive, setPositive] = useState(positiveMessage || '');
    const [negative, setNegative] = useState(negativeMessage || '');

    const handleSave = useCallback((e) => {
        e.stopPropagation();
        onSaveOutcome?.(positive, negative);
    }, [onSaveOutcome, positive, negative]);

    if (!expanded) {
        return (
            <div
                className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-zinc-700 rounded-lg border border-gray-200 dark:border-zinc-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                onClick={() => setExpanded(true)}
            >
                <Flag className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Outcome</span>
                <ChevronDown className="h-3 w-3 text-gray-400 ml-auto" />
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-zinc-800 rounded-lg border-2 border-gray-300 dark:border-zinc-600 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-100 dark:bg-zinc-700 border-b border-gray-200 dark:border-zinc-700">
                <div className="flex items-center gap-2">
                    <Flag className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Outcome</span>
                </div>
                <button className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-600" onClick={() => setExpanded(false)}>
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                </button>
            </div>
            <div className="p-4 space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Completed message</label>
                    <textarea
                        value={positive}
                        onChange={(e) => setPositive(e.target.value)}
                        rows={2}
                        placeholder="Your request has been completed."
                        className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Closed message</label>
                    <textarea
                        value={negative}
                        onChange={(e) => setNegative(e.target.value)}
                        rows={2}
                        placeholder="Your request has been closed."
                        className="block w-full rounded-md border-0 py-1.5 px-2.5 text-sm text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-blue-600"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
                <Button size="sm" onClick={handleSave}>
                    <Save className="mr-1.5 h-3 w-3" />
                    Save
                </Button>
            </div>
        </div>
    );
}

// ─── FlowEditor (main) ─────────────────────────────────────────────

export default function FlowEditor({
    steps,
    taskFlow,
    taskFlowId,
    paymentsEnabled,
    sharepointEnabled,
    onRefresh,
    onDeleteStep,
    onAddField,
    onEditField,
    onDeleteField,
    onAddCondition,
    onAddAutoOutcome,
    onEditCondition,
    onEditAutoOutcome,
}) {
    const [expandedNodeId, setExpandedNodeId] = useState(null);
    const [adminUsers, setAdminUsers] = useState([]);

    useEffect(() => {
        getAPI('admin/users').then(res => {
            if (res.response.ok && Array.isArray(res.data)) {
                setAdminUsers(res.data);
            }
        }).catch(() => {});
    }, []);

    // ── Reorder step up/down ──
    const handleReorder = useCallback(async (stepId, direction) => {
        const newOrder = computeReorderedStepIds(steps, stepId, direction);
        try {
            const response = await postReorderSteps(taskFlowId, newOrder);
            if (response.response.ok) {
                toast.success('Steps reordered.');
                onRefresh?.();
            } else {
                toast.error('Failed to reorder steps.');
            }
        } catch {
            toast.error('Failed to reorder steps.');
        }
    }, [steps, taskFlowId, onRefresh]);

    // ── Quick inline update ──
    const handleQuickUpdate = useCallback(async (stepId, values) => {
        const step = steps.find(s => s.id === stepId);
        if (!step) return;
        try {
            const body = {
                id: stepId,
                task_flow_id: taskFlowId,
                name: values.name,
                description: values.description,
                actor_type: values.actor_type,
                step_type: step.step_type,
                step_config: values.step_config !== undefined ? values.step_config : step.step_config,
                is_required: step.is_required,
                requires_previous_step_complete: step.requires_previous_step_complete,
                blocks_tenant_until_complete: step.blocks_tenant_until_complete,
                help_url: values.help_url || null,
                help_text: values.help_text || null,
                conditional_logic: step.conditional_logic || null,
                auto_outcome_rules: step.auto_outcome_rules || null,
                requires_admin_approval: values.requires_admin_approval !== undefined ? values.requires_admin_approval : (step.requires_admin_approval ?? false),
                on_complete_reassign_admin_user_id: values.on_complete_reassign_admin_user_id !== undefined ? values.on_complete_reassign_admin_user_id : (step.on_complete_reassign_admin_user_id ?? null),
            };
            const response = await postUpdateStep(body);
            if (response.response.ok) {
                toast.success('Step updated.');
                setExpandedNodeId(null);
                onRefresh?.();
            } else {
                toast.error('Failed to update step.');
            }
        } catch {
            toast.error('Failed to update step.');
        }
    }, [steps, taskFlowId, onRefresh]);

    // ── Save outcome messages ──
    const handleSaveOutcome = useCallback(async (positiveMessage, negativeMessage) => {
        if (!taskFlow) return;
        try {
            const response = await postUpdateTaskFlow({
                id: taskFlow.id,
                name: taskFlow.name,
                description: taskFlow.description,
                tenant_requestable: taskFlow.tenant_requestable,
                is_onboarding: taskFlow.is_onboarding,
                admin_assignable: taskFlow.admin_assignable,
                has_deadline: taskFlow.has_deadline,
                deadline_days: taskFlow.deadline_days,
                deadline_type: taskFlow.deadline_type,
                help_url: taskFlow.help_url,
                help_text: taskFlow.help_text,
                completion_message: taskFlow.completion_message,
                positive_outcome_message: positiveMessage || null,
                negative_outcome_message: negativeMessage || null,
            });
            if (response.response.ok) {
                toast.success('Outcome saved.');
                onRefresh?.();
            } else {
                toast.error('Failed to save outcome.');
            }
        } catch {
            toast.error('Failed to save outcome.');
        }
    }, [taskFlow, onRefresh]);

    // ── Create a new step ──
    const handleCreateStep = useCallback(async (stepType = 'standard', conditionalLogic = null, position = null) => {
        const nameMap = { standard: 'New Step', payment: 'New Payment Step', document_upload: 'New Document Upload Step', document_signing: 'New Document Signing Step' };
        try {
            const body = {
                task_flow_id: taskFlowId,
                name: nameMap[stepType] || 'New Step',
                actor_type: 'tenant',
                step_type: stepType,
            };

            if (position != null) {
                body.position = position;
            }

            if (stepType === 'payment') {
                body.step_config = { provider: 'allpay', description: null, client_code: null };
            } else if (stepType === 'document_upload') {
                const sitesRes = await getSharepointSites();
                const sites = sitesRes?.data?.sites || sitesRes?.data || [];
                if (!Array.isArray(sites) || sites.length === 0) {
                    toast.error('No SharePoint sites available. Configure SharePoint first.');
                    return;
                }
                const firstSite = sites[0];
                const libsRes = await getSharepointLibraries(firstSite.id);
                const libs = libsRes?.data?.libraries || libsRes?.data || [];
                if (!Array.isArray(libs) || libs.length === 0) {
                    toast.error('No SharePoint libraries found for the default site.');
                    return;
                }
                body.step_config = {
                    provider: 'sharepoint',
                    sharepoint_site_id: firstSite.id,
                    sharepoint_library_id: libs[0].id,
                    allowed_types: ['pdf', 'jpg', 'png', 'doc', 'docx', 'xls', 'xlsx'],
                    max_size_mb: 20,
                    max_files: 5,
                };
            } else if (stepType === 'document_signing') {
                // Check Adobe Sign integration is enabled and connected
                const intRes = await getAPI('integrations?integration=adobe_sign');
                if (!intRes.response.ok || intRes.data?.enabled !== 1) {
                    toast.error('Adobe Sign integration is not enabled. Enable it in Integrations first.');
                    return;
                }
                const connRes = await getAPI('admin/adobesign/connection');
                if (!connRes.response.ok || !connRes.data?.connected) {
                    toast.error('Adobe Sign is not connected. Complete the connection in Integrations first.');
                    return;
                }
                body.step_config = { provider: 'adobesign', template_id: null, template_name: null, field_mappings: [] };
            }

            if (conditionalLogic && conditionalLogic.length > 0) {
                body.conditional_logic = conditionalLogic;
            }

            const response = await postAddStepToTaskFlow(body);
            if (response.response.ok) {
                toast.success('Step created.');
                onRefresh?.();
            } else {
                toast.error('Failed to create step.');
            }
        } catch {
            toast.error('Failed to create step.');
        }
    }, [taskFlowId, onRefresh]);

    // ── Sort steps ──
    const sortedSteps = useMemo(() =>
        [...steps].sort((a, b) => a.position - b.position),
    [steps]);

    // ── Shared step callbacks ──
    const stepCallbacks = {
        onExpand: (stepId) => setExpandedNodeId(stepId),
        onCollapse: () => setExpandedNodeId(null),
        onDelete: (step) => onDeleteStep?.(step),
        onMoveUp: (stepId) => handleReorder(stepId, 'up'),
        onMoveDown: (stepId) => handleReorder(stepId, 'down'),
        onAddField: (step) => onAddField?.(step),
        onEditField: (field) => onEditField?.(field),
        onDeleteField: (field) => onDeleteField?.(field),
        onQuickUpdate: handleQuickUpdate,
        onAddCondition: (step) => onAddCondition?.(step),
        onEditCondition: (step) => onEditCondition?.(step),
        onAddAutoOutcome: (step) => onAddAutoOutcome?.(step),
        onEditAutoOutcome: (step, ruleIndex) => onEditAutoOutcome?.(step, ruleIndex),
    };

    // ── Render ──
    return (
        <div className="space-y-2">
            {/* Start indicator */}
            <div className="flex items-center justify-center gap-2 py-3 px-4 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-200 dark:ring-emerald-800" />
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Flow Start</span>
            </div>

            {/* Add step at very top */}
            <AddStepButton
                paymentsEnabled={paymentsEnabled}
                sharepointEnabled={sharepointEnabled}
                onAddStep={handleCreateStep}
                insertPosition={0}
            />

            {/* Steps */}
            {sortedSteps.map((step, idx) => (
                <div key={step.id}>
                    <StepRow
                        step={step}
                        stepNumber={idx + 1}
                        totalSteps={sortedSteps.length}
                        allSteps={steps}
                        isExpanded={expandedNodeId === step.id}
                        adminUsers={adminUsers}
                        {...stepCallbacks}
                    />
                    <AddStepButton
                        paymentsEnabled={paymentsEnabled}
                        sharepointEnabled={sharepointEnabled}
                        onAddStep={handleCreateStep}
                        insertPosition={step.position + 1}
                    />
                </div>
            ))}

            {/* Outcome */}
            <OutcomeCard
                positiveMessage={taskFlow?.positive_outcome_message || ''}
                negativeMessage={taskFlow?.negative_outcome_message || ''}
                onSaveOutcome={handleSaveOutcome}
            />
        </div>
    );
}
